import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { businessService, normalizeCatalogueList } from '../../services/businessService';
import { useToast } from '../../contexts/ToastContext';
import ImageUpload from '../common/ImageUpload';

/**
 * Catalogue section for Business create/edit screens.
 *
 * Inputs
 * - mode: 'create' | 'edit'.
 * - businessId: required in 'edit' (numeric ID). May be null in 'create' (drafts only).
 * - onDraftChange: callback fired on every draft mutation in 'create' mode.
 *     Receives { kind: 'products'|'services'|null, items: Array<DraftItem> }.
 *     Parent batch-creates these after the business is created.
 *
 * Behavior
 * - Mutual exclusivity: only Products OR Services are shown. The other side is
 *   hidden until the current list is empty (or both empty), per product spec.
 * - Edit mode: live CRUD against existing business API endpoints.
 * - Create mode: drafts kept in component state; parent persists them post-create.
 */

const PRODUCT_CATEGORIES = [
  { value: 'electronics', label: 'Electronics' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'food', label: 'Food & Beverages' },
  { value: 'health', label: 'Health & Beauty' },
  { value: 'home', label: 'Home & Garden' },
  { value: 'sports', label: 'Sports & Outdoors' },
  { value: 'books', label: 'Books & Media' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'other', label: 'Other' },
];

const SERVICE_CATEGORIES = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'beauty', label: 'Beauty & Wellness' },
  { value: 'fitness', label: 'Fitness & Sports' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'repair', label: 'Repair & Maintenance' },
  { value: 'cleaning', label: 'Cleaning Services' },
  { value: 'transport', label: 'Transportation' },
  { value: 'legal', label: 'Legal Services' },
  { value: 'financial', label: 'Financial Services' },
  { value: 'other', label: 'Other' },
];

const emptyProductDraft = () => ({
  name: '',
  description: '',
  category: 'other',
  price: '',
  stock_quantity: 0,
  is_available: true,
  is_menu_item: false,
  sku: '',
});

const emptyServiceDraft = () => ({
  name: '',
  description: '',
  category: 'other',
  price: '',
  duration: '',
  is_available: true,
  booking_required: false,
});

const formatCurrency = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `PKR ${num.toFixed(2)}`;
};

// Best-effort image URL extractor used for tiles when image is a File or remote object.
const previewImageUrl = (img) => {
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (typeof File !== 'undefined' && img instanceof File) return URL.createObjectURL(img);
  return img.thumbnail || img.medium_image || img.original_image || null;
};

const KindCard = ({ icon, title, helper, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="group flex flex-1 items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left transition hover:border-blue-400 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
  >
    <span
      aria-hidden
      className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-2xl text-blue-600 group-hover:bg-blue-100"
    >
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block text-base font-semibold text-gray-900">{title}</span>
      <span className="mt-1 block text-sm text-gray-500">{helper}</span>
    </span>
  </button>
);

// Inline editor for one item; supports both products and services.
const ItemEditor = ({
  kind,
  initialValues,
  initialImages,
  saving,
  onCancel,
  onSubmit,
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [images, setImages] = useState(initialImages || []);

  useEffect(() => {
    setFormData(initialValues);
    setImages(initialImages || []);
  }, [initialValues, initialImages]);

  const categories = kind === 'products' ? PRODUCT_CATEGORIES : SERVICE_CATEGORIES;

  const handleField = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = () => {
    if (!formData.name || !String(formData.name).trim()) return;
    onSubmit({ data: formData, images });
  };

  const isProduct = kind === 'products';
  const accentBtn = isProduct
    ? 'bg-blue-600 hover:bg-blue-700'
    : 'bg-emerald-600 hover:bg-emerald-700';

  return (
    <div
      role="group"
      aria-label={isProduct ? 'Product details' : 'Service details'}
      className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:p-5"
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isProduct ? 'Product name *' : 'Service name *'}
        </label>
        <input
          type="text"
          name="name"
          required
          value={formData.name}
          onChange={handleField}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={isProduct ? 'e.g., Organic Honey 500g' : 'e.g., Haircut & Styling'}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          rows={2}
          value={formData.description}
          onChange={handleField}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={isProduct ? 'What does this item include?' : 'What does this service include?'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleField}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Price (PKR)</label>
          <input
            type="number"
            name="price"
            min="0"
            step="0.01"
            value={formData.price}
            onChange={handleField}
            placeholder="0.00"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {isProduct ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stock quantity</label>
            <input
              type="number"
              name="stock_quantity"
              min="0"
              value={formData.stock_quantity}
              onChange={handleField}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SKU (optional)</label>
            <input
              type="text"
              name="sku"
              value={formData.sku || ''}
              onChange={handleField}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Internal product SKU"
            />
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
          <input
            type="text"
            name="duration"
            value={formData.duration || ''}
            onChange={handleField}
            placeholder="e.g., 1 hour, 30 minutes"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <label className="inline-flex items-center text-sm text-gray-700">
          <input
            type="checkbox"
            name="is_available"
            checked={!!formData.is_available}
            onChange={handleField}
            className="h-4 w-4 mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          {isProduct ? 'Available for purchase' : 'Available for booking'}
        </label>
        {isProduct ? (
          <label className="inline-flex items-center text-sm text-gray-700">
            <input
              type="checkbox"
              name="is_menu_item"
              checked={!!formData.is_menu_item}
              onChange={handleField}
              className="h-4 w-4 mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            Menu item (restaurants/cafes)
          </label>
        ) : (
          <label className="inline-flex items-center text-sm text-gray-700">
            <input
              type="checkbox"
              name="booking_required"
              checked={!!formData.booking_required}
              onChange={handleField}
              className="h-4 w-4 mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            Booking required
          </label>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isProduct ? 'Product images' : 'Service images'}
        </label>
        <ImageUpload selectedImages={images} onImagesChange={setImages} maxImages={3} />
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={handleSave}
          className={`px-4 py-2 rounded-md text-white transition-colors disabled:opacity-50 ${accentBtn}`}
        >
          {saving ? 'Saving…' : (isProduct ? 'Save product' : 'Save service')}
        </button>
      </div>
    </div>
  );
};

// Compact summary tile for one product/service.
const ItemTile = ({ kind, item, onEdit, onDelete, deleting }) => {
  const isProduct = kind === 'products';
  const thumbSrc = previewImageUrl(
    Array.isArray(item.images) ? item.images[0] : (item._draftImages && item._draftImages[0]) || null,
  );
  const price = formatCurrency(item.price);
  return (
    <li className="flex items-stretch gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
        {thumbSrc ? (
          <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
        ) : (
          <span aria-hidden className="text-2xl">{isProduct ? '🛍️' : '🛠️'}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{item.name || 'Untitled'}</p>
            <p className="text-xs text-gray-500 truncate">{item.category}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={onEdit}
              className="px-2 py-1 text-xs text-blue-700 hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="px-2 py-1 text-xs text-red-600 hover:underline disabled:opacity-50"
            >
              {deleting ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
          {price && <span>{price}</span>}
          {isProduct && item.stock_quantity != null && (
            <span>Stock: {item.stock_quantity}</span>
          )}
          {!isProduct && item.duration && (
            <span>Duration: {item.duration}</span>
          )}
          {!item.is_available && (
            <span className="text-amber-700">Not available</span>
          )}
        </div>
        {item.description && (
          <p className="mt-1 text-xs text-gray-600 line-clamp-2">{item.description}</p>
        )}
      </div>
    </li>
  );
};

const BusinessProductsServicesSection = ({
  mode = 'create',
  businessId = null,
  onDraftChange,
}) => {
  const { showSuccess, showError } = useToast();
  const isEdit = mode === 'edit';

  const [kind, setKind] = useState(null); // 'products' | 'services' | null
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editingId, setEditingId] = useState(null); // numeric ID, draft tempId, or 'new'
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const draftCounter = useRef(0);

  // Notify parent of draft changes (create mode only).
  useEffect(() => {
    if (!isEdit && typeof onDraftChange === 'function') {
      onDraftChange({ kind, items: drafts });
    }
  }, [isEdit, kind, drafts, onDraftChange]);

  // Edit mode: load existing products & services to determine current kind.
  useEffect(() => {
    if (!isEdit || !businessId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingList(true);
      try {
        const [productsRes, servicesRes] = await Promise.all([
          businessService.getProducts(businessId).catch(() => ({ results: [] })),
          businessService.getServices(businessId).catch(() => ({ results: [] })),
        ]);
        if (cancelled) return;
        const productList = normalizeCatalogueList(productsRes);
        const serviceList = normalizeCatalogueList(servicesRes);

        if (productList.length > 0 && serviceList.length === 0) {
          setKind('products');
          setItems(productList);
        } else if (serviceList.length > 0 && productList.length === 0) {
          setKind('services');
          setItems(serviceList);
        } else if (productList.length === 0 && serviceList.length === 0) {
          setKind(null);
          setItems([]);
        } else {
          // Legacy: both exist. Default to products tab, but allow either; the section
          // intentionally hides the inactive side to keep the spec promise.
          setKind('products');
          setItems(productList);
        }
      } catch (err) {
        console.error('Error loading business catalogue', err);
        showError('Failed to load products and services.');
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isEdit, businessId, showError]);

  const liveListRefresh = useCallback(async () => {
    if (!isEdit || !businessId || !kind) return;
    try {
      const res = kind === 'products'
        ? await businessService.getProducts(businessId)
        : await businessService.getServices(businessId);
      setItems(normalizeCatalogueList(res));
    } catch (err) {
      console.error('Failed to refresh catalogue', err);
    }
  }, [isEdit, businessId, kind]);

  const visibleItems = useMemo(() => (isEdit ? items : drafts), [isEdit, items, drafts]);

  const isListEmpty = visibleItems.length === 0;
  const canSwitchKind = isListEmpty;

  const pickKind = (next) => {
    if (!isListEmpty) return;
    setKind(next);
    setEditingId('new');
  };

  const startAdd = () => setEditingId('new');

  const startEdit = (item) => {
    const key = item.id ?? item._tempId;
    setEditingId(key);
  };

  const cancelEditor = () => setEditingId(null);

  const handleSubmitEditor = async ({ data, images }) => {
    if (!kind) return;

    // CREATE mode: store drafts; persist when business is saved.
    if (!isEdit) {
      if (editingId === 'new') {
        draftCounter.current += 1;
        const tempId = `draft-${draftCounter.current}`;
        setDrafts((prev) => [...prev, { ...data, _tempId: tempId, _draftImages: images }]);
      } else {
        setDrafts((prev) => prev.map((d) => (
          d._tempId === editingId ? { ...data, _tempId: d._tempId, _draftImages: images } : d
        )));
      }
      setEditingId(null);
      showSuccess(
        editingId === 'new'
          ? (kind === 'products' ? 'Product added to list.' : 'Service added to list.')
          : (kind === 'products' ? 'Product updated.' : 'Service updated.'),
      );
      return;
    }

    // EDIT mode: persist immediately.
    if (!businessId) {
      showError('Cannot save: business ID is missing.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...data, images };
      if (editingId === 'new') {
        if (kind === 'products') {
          await businessService.createProduct(businessId, payload);
        } else {
          await businessService.createService(businessId, payload);
        }
        showSuccess(kind === 'products' ? 'Product added.' : 'Service added.');
      } else {
        if (kind === 'products') {
          await businessService.updateProduct(businessId, editingId, payload);
        } else {
          await businessService.updateService(businessId, editingId, payload);
        }
        showSuccess(kind === 'products' ? 'Product updated.' : 'Service updated.');
      }
      await liveListRefresh();
      setEditingId(null);
    } catch (err) {
      console.error('Failed to save item', err);
      showError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!isEdit) {
      setDrafts((prev) => prev.filter((d) => d._tempId !== item._tempId));
      return;
    }
    if (!businessId || !item.id) return;
    const ok = window.confirm(`Remove "${item.name}"?`);
    if (!ok) return;
    setDeletingId(item.id);
    try {
      if (kind === 'products') {
        await businessService.deleteProduct(businessId, item.id);
      } else {
        await businessService.deleteService(businessId, item.id);
      }
      showSuccess('Removed.');
      await liveListRefresh();
    } catch (err) {
      console.error('Failed to remove item', err);
      showError('Failed to remove item. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const editingItem = useMemo(() => {
    if (editingId === null) return null;
    if (editingId === 'new') {
      return {
        initialValues: kind === 'products' ? emptyProductDraft() : emptyServiceDraft(),
        initialImages: [],
      };
    }
    if (isEdit) {
      const item = items.find((i) => i.id === editingId);
      if (!item) return null;
      const base = kind === 'products' ? emptyProductDraft() : emptyServiceDraft();
      return {
        initialValues: {
          ...base,
          ...item,
          price: item.price ?? '',
          stock_quantity: item.stock_quantity ?? 0,
          sku: item.sku ?? '',
          duration: item.duration ?? '',
        },
        initialImages: [],
      };
    }
    const draft = drafts.find((d) => d._tempId === editingId);
    if (!draft) return null;
    return {
      initialValues: { ...draft },
      initialImages: draft._draftImages || [],
    };
  }, [editingId, isEdit, items, drafts, kind]);

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Products or services</h2>
        <p className="text-sm text-gray-500">
          A business sells either products or offers services. Pick one — we'll show only what you choose.
        </p>
      </header>

      {loadingList ? (
        <div className="py-8 text-center text-sm text-gray-500">Loading catalogue…</div>
      ) : kind === null ? (
        <div className="flex flex-col sm:flex-row gap-3">
          <KindCard
            icon="🛍️"
            title="Add products"
            helper="Physical items, menu items, retail goods."
            onClick={() => pickKind('products')}
          />
          <KindCard
            icon="🛠️"
            title="Add services"
            helper="Bookable services, consultations, repairs."
            onClick={() => pickKind('services')}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <span className="text-sm font-medium text-gray-900">
                {kind === 'products' ? 'Products' : 'Services'}
              </span>
              <span className="ml-2 text-xs text-gray-500">
                ({visibleItems.length} {visibleItems.length === 1 ? 'item' : 'items'})
              </span>
            </div>
            <div className="flex items-center gap-2">
              {canSwitchKind && (
                <button
                  type="button"
                  onClick={() => {
                    setKind(null);
                    setEditingId(null);
                  }}
                  className="text-xs text-gray-600 hover:text-gray-900 hover:underline"
                >
                  Switch to {kind === 'products' ? 'services' : 'products'}
                </button>
              )}
              {editingId === null && (
                <button
                  type="button"
                  onClick={startAdd}
                  className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  + Add {kind === 'products' ? 'product' : 'service'}
                </button>
              )}
            </div>
          </div>

          {visibleItems.length > 0 && (
            <ul className="space-y-2">
              {visibleItems.map((item) => (
                <ItemTile
                  key={item.id ?? item._tempId}
                  kind={kind}
                  item={item}
                  onEdit={() => startEdit(item)}
                  onDelete={() => handleDelete(item)}
                  deleting={deletingId === item.id}
                />
              ))}
            </ul>
          )}

          {visibleItems.length === 0 && editingId === null && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
              <p className="text-sm text-gray-600">
                No {kind} added yet. Use “Add {kind === 'products' ? 'product' : 'service'}” to get started.
              </p>
            </div>
          )}

          {editingItem && (
            <ItemEditor
              kind={kind}
              initialValues={editingItem.initialValues}
              initialImages={editingItem.initialImages}
              saving={saving}
              onCancel={cancelEditor}
              onSubmit={handleSubmitEditor}
            />
          )}

          {!isEdit && drafts.length > 0 && (
            <p className="text-xs text-gray-500">
              These {kind} will be added when you create the business.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default BusinessProductsServicesSection;
